<template>
  <div class="entity-search-widget">
    <!-- Trigger Button -->
    <button @click="openModal" class="btn-primary-sm">
      <FontAwesomeIcon :icon="['fas', 'folder-tree']" />
      Select Entities
      <span v-if="selectedCount > 0" class="bg-white text-blue-600 px-2 py-0.5 rounded-full text-sm font-bold ml-1">
        {{ selectedCount }}
      </span>
    </button>

    <!-- Modal -->
    <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(0,0,0,0.3);">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 flex flex-col" style="border: 1px solid #E8E9E9; max-height: 85vh;">

        <!-- Header -->
        <div class="px-5 py-3 flex items-center justify-between flex-shrink-0" style="border-bottom: 1px solid #E8E9E9;">
          <h3 class="text-sm font-semibold" style="color: #353537;">Select Entities</h3>
          <button @click="cancelSelection" class="text-gray-400 hover:text-gray-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Filter Row -->
        <div class="px-5 py-3 flex items-end gap-3 flex-shrink-0" style="border-bottom: 1px solid #F3F4F6;">
          <!-- Entity Type (primary, required) -->
          <div class="flex-1">
            <label class="block text-xs mb-1" style="color: #626264;">Entity Type</label>
            <select
              v-model="entityTypeId"
              @change="onEntityTypeChange"
              class="w-full text-xs border rounded-lg px-2.5 py-1.5"
              style="border-color: #E8E9E9; color: #353537;"
            >
              <option :value="null">— select —</option>
              <option v-for="t in entityTypes" :key="t.id" :value="t.id">{{ t.type_name }}</option>
            </select>
          </div>

          <!-- Parent Type (optional narrowing) -->
          <div class="flex-1">
            <label class="block text-xs mb-1" style="color: #626264;">Parent Type <span style="color: #A5A5A6;">(optional)</span></label>
            <select
              v-model="parentTypeId"
              @change="onParentTypeChange"
              :disabled="!entityTypeId"
              class="w-full text-xs border rounded-lg px-2.5 py-1.5"
              style="border-color: #E8E9E9; color: #353537;"
            >
              <option :value="null">— any —</option>
              <option v-for="t in entityTypes" :key="t.id" :value="t.id">{{ t.type_name }}</option>
            </select>
          </div>

          <!-- Parent Entity (optional narrowing) -->
          <div class="flex-1">
            <label class="block text-xs mb-1" style="color: #626264;">Parent <span style="color: #A5A5A6;">(optional)</span></label>
            <select
              v-model="parentId"
              @change="loadChildren"
              :disabled="!parentTypeId"
              class="w-full text-xs border rounded-lg px-2.5 py-1.5"
              style="border-color: #E8E9E9; color: #353537;"
            >
              <option :value="null">{{ parentTypeId ? (parentEntities.length ? '— any —' : 'Loading...') : '— select parent type first —' }}</option>
              <option v-for="e in parentEntities" :key="e.id" :value="e.id">{{ e.name }}</option>
            </select>
          </div>

          <!-- Name Search -->
          <div class="flex-1">
            <label class="block text-xs mb-1" style="color: #626264;">Search</label>
            <input
              v-model="searchText"
              @input="debouncedSearch"
              placeholder="Filter by name..."
              class="w-full text-xs border rounded-lg px-2.5 py-1.5"
              style="border-color: #E8E9E9; color: #353537;"
            />
          </div>
        </div>

        <!-- Selection Bar -->
        <div class="px-5 py-2 flex items-center justify-between flex-shrink-0" style="background: #F9FAFB; border-bottom: 1px solid #F3F4F6;">
          <div class="flex items-center gap-3">
            <span class="text-xs" style="color: #626264;">
              {{ filteredChildren.length }} entities
            </span>
            <span v-if="selectedIds.length" class="text-xs font-medium" style="color: #F76734;">
              {{ selectedIds.length }} selected
            </span>
          </div>
          <div class="flex gap-2">
            <button @click="selectAllVisible" class="text-xs px-2 py-1 rounded" style="color: #626264; border: 1px solid #E8E9E9;" :disabled="!filteredChildren.length">
              Select All
            </button>
            <button @click="clearSelection" class="text-xs px-2 py-1 rounded" style="color: #626264; border: 1px solid #E8E9E9;" :disabled="!selectedIds.length">
              Clear
            </button>
          </div>
        </div>

        <!-- Entity List -->
        <div class="flex-1 overflow-y-auto px-5">
          <div v-if="loading" class="py-12 text-center text-xs" style="color: #626264;">Loading...</div>
          <div v-else-if="!entityTypeId" class="py-12 text-center text-xs" style="color: #A5A5A6;">
            Select an entity type to see entities
          </div>
          <div v-else-if="!children.length" class="py-12 text-center text-xs" style="color: #A5A5A6;">
            No entities found
          </div>
          <table v-else-if="children.length" class="w-full text-xs">
            <thead class="sticky top-0 z-10 bg-white">
              <tr style="border-bottom: 1px solid #E8E9E9;">
                <th class="py-2 px-1 text-left" style="width: 32px;">
                  <input
                    type="checkbox"
                    :checked="allVisibleSelected"
                    :indeterminate.prop="someVisibleSelected && !allVisibleSelected"
                    @change="toggleAllVisible"
                    class="rounded border-gray-300"
                  />
                </th>
                <th class="py-2 text-left font-medium" style="color: #626264;">Name</th>
                <th class="py-2 text-left font-medium" style="color: #626264;">Type</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entity in filteredChildren"
                :key="entity.id"
                class="hover:bg-gray-50 cursor-pointer"
                style="border-bottom: 1px solid #F3F4F6;"
                @click="toggleEntity(entity.id)"
              >
                <td class="py-2 px-1">
                  <input
                    type="checkbox"
                    :checked="selectedIds.includes(entity.id)"
                    @click.stop="toggleEntity(entity.id)"
                    class="rounded border-gray-300"
                  />
                </td>
                <td class="sensitive py-2" style="color: #353537;">{{ entity.name }}</td>
                <td class="py-2" style="color: #A5A5A6;">{{ entity.entity_type_name }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer -->
        <div class="px-5 py-3 flex items-center justify-between flex-shrink-0" style="border-top: 1px solid #E8E9E9;">
          <span class="text-xs" style="color: #626264;">{{ selectedIds.length }} selected</span>
          <div class="flex gap-3">
            <button @click="cancelSelection" class="px-4 py-1.5 text-xs rounded-lg" style="color: #626264; border: 1px solid #E8E9E9;">Cancel</button>
            <button
              @click="confirmSelection"
              class="px-4 py-1.5 text-xs text-white rounded-lg"
              :style="{ backgroundColor: selectedIds.length ? '#F76734' : '#D1D5DB' }"
              :disabled="!selectedIds.length"
            >Confirm</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'EntitySearchCareManagement',
  components: { FontAwesomeIcon },

  props: {
    initialSelection: { type: Array, default: () => [] },
  },

  emits: ['entities-selected'],

  data() {
    return {
      showModal: false,
      entityTypes: [],

      // Filter state
      entityTypeId: null,       // primary: the type of entity being selected
      parentTypeId: null,       // optional narrowing filter
      parentEntities: [],
      parentId: null,           // optional narrowing filter
      children: [],
      searchText: '',
      loading: false,

      // Selection
      selectedIds: [],
      selectedCount: 0,

      // Lookup cache for chip names
      entityNameCache: {},
    };
  },

  computed: {
    filteredChildren() {
      if (!this.searchText) return this.children;
      const q = this.searchText.toLowerCase();
      return this.children.filter(e => e.name.toLowerCase().includes(q));
    },
    allVisibleSelected() {
      return this.filteredChildren.length > 0 && this.filteredChildren.every(e => this.selectedIds.includes(e.id));
    },
    someVisibleSelected() {
      return this.filteredChildren.some(e => this.selectedIds.includes(e.id));
    },
  },

  created() {
    this.debouncedSearch = debounce(() => {}, 200); // filteredChildren is computed, no fetch needed
    if (this.initialSelection?.length) {
      this.selectedIds = [...this.initialSelection];
      this.selectedCount = this.selectedIds.length;
    }
  },

  methods: {
    openModal() {
      this.showModal = true;
      if (!this.entityTypes.length) this.loadEntityTypes();
    },

    async loadEntityTypes() {
      try {
        const { data } = await axios.get('api/entity-types/');
        this.entityTypes = (data.results || data).sort((a, b) => a.type_name.localeCompare(b.type_name));
      } catch (e) {
        console.error('Failed to load entity types:', e);
      }
    },

    // Primary selector: pick the entity type, load all of that type immediately.
    // Resets the optional parent narrowing filters.
    onEntityTypeChange() {
      this.parentTypeId = null;
      this.parentEntities = [];
      this.parentId = null;
      this.loadChildren();
    },

    // Optional narrowing: pick a parent type, load its entities for the Parent dropdown.
    async onParentTypeChange() {
      this.parentId = null;
      this.parentEntities = [];
      this.loadChildren();  // parentId cleared → falls back to all of entityTypeId
      if (!this.parentTypeId) return;

      try {
        const { data } = await axios.get('api/entities/', {
          params: { entity_type_id: this.parentTypeId, page_size: 1000 }
        });
        this.parentEntities = (data.results || data).sort((a, b) => a.name.localeCompare(b.name));
      } catch (e) {
        console.error('Failed to load parent entities:', e);
      }
    },

    // Load the entities of the chosen entity type, optionally narrowed to those
    // descending from the chosen parent.
    async loadChildren() {
      this.children = [];
      if (!this.entityTypeId) return;
      this.loading = true;

      const params = {
        entity_type_id: this.entityTypeId,
        page_size: 1000,
      };
      if (this.parentId) {
        params.parent_id = this.parentId;
        params.include_descendants = 'true';
      }

      try {
        const { data } = await axios.get('api/entities/', { params });
        this.children = (data.results || data).sort((a, b) => a.name.localeCompare(b.name));
        this._cacheNames(this.children);
      } catch (e) {
        console.error('Failed to load entities:', e);
        this.children = [];
      } finally {
        this.loading = false;
      }
    },

    _cacheNames(entities) {
      for (const e of entities) {
        this.entityNameCache[e.id] = e.name;
      }
    },

    entityName(id) {
      return this.entityNameCache[id] || id;
    },

    toggleEntity(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
    },

    toggleAllVisible() {
      if (this.allVisibleSelected) {
        const visibleIds = new Set(this.filteredChildren.map(e => e.id));
        this.selectedIds = this.selectedIds.filter(id => !visibleIds.has(id));
      } else {
        const current = new Set(this.selectedIds);
        for (const e of this.filteredChildren) {
          current.add(e.id);
        }
        this.selectedIds = Array.from(current);
      }
    },

    selectAllVisible() {
      const current = new Set(this.selectedIds);
      for (const e of this.filteredChildren) {
        current.add(e.id);
      }
      this.selectedIds = Array.from(current);
    },

    clearSelection() {
      this.selectedIds = [];
    },

    confirmSelection() {
      this.selectedCount = this.selectedIds.length;
      // Emit an id→name map (from every entity loaded this session) so the
      // parent can render chips for any entity type, not just a hardcoded one.
      const names = {};
      for (const id of this.selectedIds) {
        if (this.entityNameCache[id]) names[id] = this.entityNameCache[id];
      }
      this.$emit('entities-selected', [...this.selectedIds], names);
      this.showModal = false;
    },

    cancelSelection() {
      this.showModal = false;
    },
  },

  watch: {
    initialSelection(val) {
      this.selectedIds = val ? [...val] : [];
      this.selectedCount = this.selectedIds.length;
    },
  },
};
</script>
